import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { MetricType } from '../PortfolioHealth';
import type { RootState } from '../../redux/store';
import styles from './PortfolioCharting.module.sass';
import OHLCVChart from '../OHLCV';
import { getPortfolioBenchmarkComparison, getPortfolio, getMaxDrawDown } from 'services/portfolio';
import { isLoading } from 'redux/userReducer';
import { setMarketScore, setPortfolioScore } from '../../redux/portfolioReducer';
import PortfolioBenchmarkComparison from 'components/PortfolioBenchmarkComparison';
import PortfolioDrawdownAnalysis from 'components/PortfolioDrawdownAnalysis';
import { Card, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import Spinner from 'components/Spinner';

interface PortfolioChartingProps {
  selectedMetric: MetricType;
  activeView: 'metrics' | 'holdings';
}

interface MetricLoadingState {
  benchmark: boolean;
  drawdown: boolean;
}

interface MetricDataState {
  benchmark: any | null;
  drawdown: any | null;
}

const mapFullChainToShort = (chain: string | null): string => {
  if (!chain) return '';
  
  const chainLower = chain.toLowerCase();
  
  const mapping: { [key: string]: string } = {
    'eth-mainnet': 'eth',
    'polygon-mainnet': 'matic',
    'binance-mainnet': 'bsc',
    'avalanche-mainnet': 'avax',
    'optimism-mainnet': 'op',
    'arbitrum-mainnet': 'arb',
    'base-mainnet': 'base',
    'zksync-mainnet': 'zksync'
  };
  return mapping[chainLower] || chainLower;
};

const PortfolioCharting = ({ selectedMetric, activeView }: PortfolioChartingProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [metricLoading, setMetricLoading] = useState<MetricLoadingState>({
    benchmark: false,
    drawdown: false
  });
  
  const [metricData, setMetricData] = useState<MetricDataState>({
    benchmark: null,
    drawdown: null
  });
  
  const selectedToken = useSelector((state: RootState) => state.user.selectedToken);
  const selectedContract = useSelector((state: RootState) => state.user.selectedContract);
  const selectedChain = useSelector((state: RootState) => state.user.selectedChain);
  const selectedBenchmark = useSelector((state: RootState) => state.user.selectedBenchmark) || 'ETH';
  const portBenchData = useSelector((state: RootState) => state.portfolio.portBench);
  const maxDrawDownData = useSelector((state: RootState) => state.portfolio.maxDrawDown);
  const isPortBenchLoading = useSelector((state: RootState) => state.portfolio.loading.portBench);
  const isMaxDrawDownLoading = useSelector((state: RootState) => state.portfolio.loading.maxDrawDown);
  const chainFormatted = mapFullChainToShort(selectedChain);
  const selectedWallet = useSelector((state: RootState) => state.user.wallets[0]);
  const dataDuration = useSelector((state: RootState) => state.user.dataDuration);

  useEffect(() => {
    const fetchMetricData = async () => {
      if (activeView === 'metrics') {
        // Reset loading states
        setMetricLoading({
          benchmark: true,
          drawdown: true
        });

        // Fetch benchmark data
        getPortfolioBenchmarkComparison(dataDuration.id, undefined, undefined, selectedBenchmark)
          .then(benchmarkData => {
            if (benchmarkData?.score) {
              dispatch(setMarketScore(benchmarkData.score));
              setMetricData(prev => ({ ...prev, benchmark: benchmarkData }));
            }
          })
          .catch(error => console.error('Error fetching benchmark'))
          .finally(() => setMetricLoading(prev => ({ ...prev, benchmark: false })));

        // Fetch drawdown data
        getMaxDrawDown(selectedBenchmark)
          .then(drawdownData => {
            if (drawdownData?.score) {
              dispatch(setPortfolioScore(drawdownData.score));
              setMetricData(prev => ({ ...prev, drawdown: drawdownData }));
            }
          })
          .catch(error => console.error('Error fetching drawdown'))
          .finally(() => setMetricLoading(prev => ({ ...prev, drawdown: false })));
      }
    };

    fetchMetricData();
  }, [activeView, dataDuration, selectedWallet, selectedBenchmark, dispatch]);

  const getChartHeight = () => {
    if (isMobile) return 300;
    if (isTablet) return 400;
    return 560;
  };

  const renderBenchmarkChart = () => {
    if (metricLoading.benchmark) {
      return (
        <Card sx={{ background: '#0B1118' }}> 
          <Box sx={{ 
            height: getChartHeight(), 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
          }}>
            <Spinner />
          </Box>
        </Card>
      );
    }
  
    if (!metricData.benchmark && !portBenchData) {
      return (
        <Card>
          <Box sx={{ 
            height: getChartHeight(), 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Typography color="text.secondary">No comparison data available</Typography>
          </Box>
        </Card>
      );
    }
  
    const benchmarkData = metricData.benchmark || (Array.isArray(portBenchData) ? portBenchData[0] : portBenchData);
    
    return (
      <div className={styles.chartWrapper}>
        <PortfolioBenchmarkComparison 
          data={benchmarkData}
          height={getChartHeight()}
        />
      </div>
    );
  };

  const renderMetricChart = () => {
    if (activeView !== 'metrics') return null;

    switch (selectedMetric) {
      case 'market':
        return renderBenchmarkChart();

      case 'drawdown':
        if (metricLoading.drawdown) {
          return (
            <Card sx={{ background: '#0B1118' }}> 
              <Box sx={{ 
                height: getChartHeight(), 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
              }}>
                <Spinner />
              </Box>
            </Card>
          );
        }
      
        if (!metricData.drawdown && !maxDrawDownData) {
          return (
            <Card>
              <Box sx={{ 
                height: getChartHeight(), 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Typography color="text.secondary">No drawdown data available</Typography>
              </Box>
            </Card>
          );
        }
      
        const drawdownData = metricData.drawdown || (Array.isArray(maxDrawDownData) ? maxDrawDownData[0] : maxDrawDownData);
        
        return (
          <div className={styles.chartWrapper}>
            <PortfolioDrawdownAnalysis 
              data={drawdownData}
              height={getChartHeight()}
            />
          </div>
        );

      default:
        // Default to market comparison view
        return renderBenchmarkChart();
    }
  };

  const renderChart = () => {
    if (activeView === 'holdings') {
      if (!selectedContract || !selectedChain) {
        return (
          <div className={`${styles.emptyState} ${isMobile ? styles.mobile : ''}`}>
            Please select a token from the portfolio donut chart.
          </div>
        );
      }
      return (
        <div className={styles.chartWrapper}>
          <OHLCVChart 
            tokenAddress={selectedContract} 
            initialChain={chainFormatted}
            height={getChartHeight()}
          />
        </div>
      );
    }

    return renderMetricChart();
  };

  return (
    <div className={`${styles.container} ${isMobile ? styles.mobile : ''}`}>
      {renderChart()}
    </div>
  );
};

export default PortfolioCharting;